import {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import BootstrapTable from 'react-bootstrap-table-next';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    PaginationTotalStandalone,
    SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import { withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Button,
    Col,
    Row,
    Spinner,
    UncontrolledTooltip,
} from 'reactstrap';

import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT, INTERNATIONAL_DATE_TIME_FORMAT } from 'src/common/constants';
import DocumentManagementService, {
    getDocumentApiError,
} from 'src/modules/3rd-party-management/apis/admin/DocumentManagementService';
import {
    ThirdPartyDocumentCategories,
    ThirdPartyDocumentConfidentialityLevels,
    ThirdPartyDocumentTypes,
    ThirdPartyDocumentUploadedByTypes,
} from 'src/modules/3rd-party-management/constants';
import DateUtils from 'src/services/utils/DateUtils';
import UserUtils from 'src/services/utils/UserUtils';

import DeleteDocumentModal from './components/DeleteDocumentModal';
import DocumentFilters from './components/DocumentFilters';
import DocumentModal from './components/DocumentModal';

const DEFAULT_FILTERS = {
    pageIndex: 1,
    pageSize: 25,
    sortField: null,
    sortOrder: null,
};

const getDocumentName = (document) => (
    document?.title || document?.name || document?.originName || document?.origin_name
);

const SOURCE_LABELS = {
    upload: 'Upload',
    website: 'Website',
    confluence: 'Confluence',
};

const SYNC_STATUS_LABELS = {
    synced: 'Synced',
    skipped: 'Skipped',
    external: 'External',
    failed: 'Failed',
};

const DocumentManager = ({
    t,
    user,
    supplierId,
    productId,
    showFilters = true,
    showCreateButton = true,
    allowEdit = true,
    allowDelete = true,
    thirdPartyMode = false,
    linkedToProduct = false,
}) => {
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [documentToEdit, setDocumentToEdit] = useState(null);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [downloadProgress, setDownloadProgress] = useState({});
    const [resyncingDocumentIds, setResyncingDocumentIds] = useState({});
    const [deniedDocumentIds, setDeniedDocumentIds] = useState({});
    const [createDenied, setCreateDenied] = useState(false);

    const dateUtils = useMemo(() => new DateUtils(), []);
    const userUtils = useMemo(() => new UserUtils(), []);
    const canWriteWithoutRelation = userUtils.isAnalystAdmin(user) || userUtils.isPartner(user);
    const canWriteRows = !thirdPartyMode && Boolean(supplierId || productId || canWriteWithoutRelation);
    const confidentialityLevels = thirdPartyMode
        ? ThirdPartyDocumentConfidentialityLevels.filter((level) => (
            level.value === 'public' || level.value === 'internal'
        ))
        : ThirdPartyDocumentConfidentialityLevels;
    const rowOwnedByCurrentUser = useCallback((row) => (
        String(row?.createdBy?.id ?? '') === String(user?.id ?? '')
    ), [user?.id]);

    const documentsQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-documents-management-list',
            supplierId || null,
            productId || null,
            thirdPartyMode,
            linkedToProduct,
            filters,
        ],
        queryFn: async () => {
            const service = DocumentManagementService.getInstance();

            if (productId) {
                return await service.listByProduct(productId, filters);
            }

            if (supplierId && !thirdPartyMode) {
                return await service.listBySupplier(supplierId, filters);
            }

            return await service.list({
                ...filters,
                linkedToProduct: linkedToProduct ? 1 : undefined,
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            const apiError = getDocumentApiError(error);

            toast(apiError.message || t('An error occurred while retrieving the documents.'), {
                type: 'error',
            });
        },
    });

    const documents = useMemo(() => {
        if (Array.isArray(documentsQuery.data)) {
            return documentsQuery.data;
        }

        return documentsQuery.data?.documents || [];
    }, [documentsQuery.data]);

    const itemsCount = Number(
        documentsQuery.data?.itemsCount
        ?? documentsQuery.data?.total
        ?? documents.length
    );

    const handleDownload = useCallback(async (document) => {
        const service = DocumentManagementService.getInstance();

        setDownloadProgress((currentProgress) => ({
            ...currentProgress,
            [document.id]: 0,
        }));

        try {
            const data = await service.download(
                document.id,
                (progressEvent) => {
                    const total = progressEvent.total || document.size;

                    if (!total) return;

                    setDownloadProgress((currentProgress) => ({
                        ...currentProgress,
                        [document.id]: Math.round(
                            (progressEvent.loaded * 100) / total
                        ),
                    }));
                }
            );

            const objectUrl = window.URL.createObjectURL(new Blob([data]));
            const link = window.document.createElement('a');

            link.href = objectUrl;
            link.setAttribute('download', document.name || getDocumentName(document));
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(getDocumentApiError(error).message || t('An error occurred while downloading the file.'), {
                type: 'error',
            });
        } finally {
            setDownloadProgress((currentProgress) => {
                const nextProgress = { ...currentProgress };

                delete nextProgress[document.id];

                return nextProgress;
            });
        }
    }, [t]);

    const handleResync = useCallback(async (document) => {
        const service = DocumentManagementService.getInstance();

        setResyncingDocumentIds((currentIds) => ({
            ...currentIds,
            [document.id]: true,
        }));

        try {
            const result = await service.linkResync(document.id);

            if (result?.current_status === 'failed') {
                toast(result.error || t('The document link could not be synced.'), {
                    type: 'error',
                });
            } else {
                toast(t('Document link synced successfully.'), {
                    type: 'success',
                });
            }

            documentsQuery.refetch();
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            const apiError = getDocumentApiError(error);

            toast(apiError.message || t('An error occurred while syncing the document link.'), {
                type: 'error',
            });

            if (apiError.status === 403) {
                setDeniedDocumentIds((currentIds) => ({
                    ...currentIds,
                    [document.id]: true,
                }));
            }

            if (apiError.status === 404) {
                documentsQuery.refetch();
            }
        } finally {
            setResyncingDocumentIds((currentIds) => {
                const nextIds = { ...currentIds };

                delete nextIds[document.id];

                return nextIds;
            });
        }
    }, [documentsQuery, t]);

    const columns = useMemo(() => {
        const result = [
            {
                dataField: 'id',
                text: t('ID'),
                sort: true,
                style: {
                    width: '80px',
                },
            },
            {
                dataField: 'title',
                text: t('Title'),
                sort: true,
                style: {
                    width: '180px',
                },
                formatter: (_cell, row) => {
                    if (row.content_type === 'link') {
                        return (
                            <a
                                href={row.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-break text-wrap text-truncate"
                            >
                                {getDocumentName(row)}
                            </a>
                        );
                    }

                    return getDocumentName(row);
                },
            },
            {
                dataField: 'type',
                text: t('Type'),
                sort: false,
                style: {
                    width: '100px',
                },
                formatter: (cell) => t(
                    ThirdPartyDocumentTypes.find((item) => item.value === cell)?.title
                    || cell
                ),
            },
            {
                dataField: 'source',
                text: t('Source'),
                sort: false,
                style: {
                    width: '100px',
                },
                formatter: (cell) => t(SOURCE_LABELS[cell] || cell || 'Upload'),
            },
            {
                dataField: 'category',
                text: t('Category'),
                sort: true,
                style: {
                    width: '140px',
                },
                formatter: (cell) => t(
                    ThirdPartyDocumentCategories
                        .find((item) => item.value === cell)?.title
                    || cell
                ),
            },
            {
                dataField: 'confidentialityLevel',
                text: t('Confidentiality Level'),
                sort: true,
                style: {
                    width: '160px',
                },
                formatter: (cell) => t(
                    ThirdPartyDocumentConfidentialityLevels
                        .find((item) => item.value === cell)?.title
                    || cell
                ),
            },
            {
                dataField: 'createdAt',
                text: t('Created On'),
                sort: true,
                style: {
                    width: '120px',
                },
                formatter: (cell) => (
                    cell
                        ? dateUtils.convertDateToDate(
                            cell,
                            INTERNATIONAL_DATE_FORMAT
                        )
                        : '---'
                ),
            },
            {
                dataField: 'createdBy',
                text: t('Uploaded By'),
                sort: false,
                style: {
                    width: '180px',
                },
                formatter: (cell, row, rowIndex) => {
                    const creatorType = ThirdPartyDocumentUploadedByTypes
                        .find((item) => item.value === row?.uploadedByType);
                    const name = cell?.fullName || '---';

                    if (!creatorType) {
                        return name;
                    }

                    const tooltipId = `tpdd-uploaded-by-${row?.id ?? rowIndex}`;

                    return (
                        <span className="d-flex align-items-start justify-content-start flex-column">
                            <span>{name}</span>
                            <span
                                id={tooltipId}
                                className="badge bg-light text-dark border d-flex align-items-center"
                            >
                                <i className={`${creatorType.value === 'third_party' ? 'ri-building-line' : 'ri-shield-user-line'} me-1 font-size-16`} />
                                {t(creatorType.title)}
                            </span>
                            <UncontrolledTooltip
                                placement="top"
                                target={tooltipId}
                            >
                                {t(creatorType.description)}
                            </UncontrolledTooltip>
                        </span>
                    );
                },
            },
            {
                dataField: 'articleNumber',
                text: t('Article Number'),
                sort: false,
                style: {
                    width: '140px',
                },
                formatter: (cell) => cell || '---',
            },
            {
                dataField: 'internalSku',
                text: t('Internal SKU'),
                sort: false,
                style: {
                    width: '140px',
                },
                formatter: (cell) => cell || '---',
            },
            {
                dataField: 'sync_status',
                text: t('Sync Status'),
                sort: false,
                style: {
                    width: '120px',
                },
                formatter: (_cell, row) => {
                    const status = row.confluence_meta?.sync_status;

                    return status
                        ? t(SYNC_STATUS_LABELS[status] || status)
                        : '---';
                },
            },
            {
                dataField: 'last_synced',
                text: t('Last Synced'),
                sort: false,
                style: {
                    width: '120px',
                },
                formatter: (_cell, row) => {
                    const syncedAt = row.confluence_meta?.synced_at;

                    return syncedAt
                        ? dateUtils.convertDateToDate(
                            syncedAt,
                            INTERNATIONAL_DATE_TIME_FORMAT
                        )
                        : '---';
                },
            },
            {
                dataField: 'last_updated',
                text: t('Last Updated'),
                sort: false,
                style: {
                    width: '120px',
                },
                formatter: (_cell, row) => {
                    const updatedAt =
                        row.confluence_meta?.content_updated_at;

                    return updatedAt
                        ? dateUtils.convertDateToDate(
                            updatedAt,
                            INTERNATIONAL_DATE_TIME_FORMAT
                        )
                        : '---';
                },
            },
        ];

        result.push({
            dataField: 'actions',
            text: t('Actions'),
            sort: false,
            style: {
                width: '220px',
            },
            formatter: (_cell, row) => {
                const progress = downloadProgress[row.id];
                const isDownloading = progress !== undefined;
                const isResyncing = Boolean(resyncingDocumentIds[row.id]);
                const isLink = row.content_type === 'link';
                const rowIsWritable = !deniedDocumentIds[row.id] && (
                    thirdPartyMode ? rowOwnedByCurrentUser(row) : canWriteRows
                );

                return (
                    <div className="d-flex align-items-center">
                        {allowDelete && rowIsWritable && (
                            <>
                                <Button
                                    id={`delete-tpdd-document-${row.id}`}
                                    color="link"
                                    className="waves-effect me-1 text-danger"
                                    onClick={() => setDocumentToDelete(row)}
                                >
                                    <i className="ri-delete-bin-line font-size-22" />
                                </Button>
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`delete-tpdd-document-${row.id}`}
                                >
                                    {t('Delete Document')}
                                </UncontrolledTooltip>
                            </>
                        )}

                        {allowEdit && rowIsWritable && (
                            <>
                                <Button
                                    id={`edit-tpdd-document-${row.id}`}
                                    color="primary"
                                    outline
                                    className="border-0 waves-effect me-1"
                                    onClick={() => setDocumentToEdit(row)}
                                >
                                    <i className="ri-pencil-line font-size-22" />
                                </Button>
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`edit-tpdd-document-${row.id}`}
                                >
                                    {t('Edit Document')}
                                </UncontrolledTooltip>
                            </>
                        )}

                        {allowEdit && rowIsWritable && isLink && (
                            <>
                                <Button
                                    id={`resync-tpdd-document-${row.id}`}
                                    color="link"
                                    className="waves-effect me-1"
                                    onClick={() => handleResync(row)}
                                    disabled={isResyncing}
                                >
                                    {isResyncing ? (
                                        <Spinner
                                            size="sm"
                                            color="primary"
                                        />
                                    ) : (
                                        <i className="ri-refresh-line font-size-22" />
                                    )}
                                </Button>
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`resync-tpdd-document-${row.id}`}
                                >
                                    {t('Resync')}
                                </UncontrolledTooltip>
                            </>
                        )}

                        {row.content_type === 'file' ? (
                            <>
                                <Button
                                    id={`download-tpdd-document-${row.id}`}
                                    color="link"
                                    className="waves-effect me-1"
                                    onClick={() => handleDownload(row)}
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? (
                                        <>
                                            <Spinner
                                                size="sm"
                                                color="primary"
                                                className="me-1"
                                            />
                                            {progress}%
                                        </>
                                    ) : (
                                        <i className="ri-download-line font-size-22" />
                                    )}
                                </Button>
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`download-tpdd-document-${row.id}`}
                                >
                                    {t('Download Document')}
                                </UncontrolledTooltip>
                            </>
                        ) : (
                            <>
                                <Button
                                    id={`open-tpdd-document-${row.id}`}
                                    color="link"
                                    href={row.path}
                                    target="_blank"
                                    tag="a"
                                    rel="noopener noreferrer"
                                >
                                    <i className="ri-external-link-line font-size-22" />
                                </Button>
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`open-tpdd-document-${row.id}`}
                                >
                                    {t('View Document (opens in a new tab)')}
                                </UncontrolledTooltip>
                            </>
                        )}
                    </div>
                );
            },
        });

        return result;
    }, [
        allowDelete,
        allowEdit,
        canWriteRows,
        dateUtils,
        rowOwnedByCurrentUser,
        thirdPartyMode,
        deniedDocumentIds,
        downloadProgress,
        handleDownload,
        handleResync,
        resyncingDocumentIds,
        t,
    ]);

    const handleTableChange = (type, data) => {
        if (type === 'pagination') {
            setFilters((currentFilters) => ({
                ...currentFilters,
                pageIndex: data.page,
                pageSize: data.sizePerPage,
            }));
        }

        if (type === 'sort') {
            setFilters((currentFilters) => ({
                ...currentFilters,
                sortField: data.sortField,
                sortOrder: data.sortOrder,
            }));
        }
    };

    const handleFilterChange = useCallback((nextFilters) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            ...nextFilters,
            pageIndex: 1,
        }));
    }, []);

    const hideDeniedDocument = (document) => {
        if (!document?.id) {
            return;
        }

        setDeniedDocumentIds((currentIds) => ({
            ...currentIds,
            [document.id]: true,
        }));
    };

    const closeDocumentModal = () => {
        setShowDocumentModal(false);
        setDocumentToEdit(null);
    };

    const noDataIndication = () => {
        if (!documentsQuery.isFetched) {
            return (
                <div className="text-center py-4">
                    <Spinner
                        color="primary"
                        size="sm"
                        className="me-2"
                    />
                    {`${t('Loading')}...`}
                </div>
            );
        }

        if (documents.length === 0) {
            return (
                <div className="alert m-0" role="alert">
                    <p className="text-center mb-0">
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            );
        }

        return <div />;
    };

    return (
        <>
            <DocumentModal
                isOpen={showDocumentModal || !!documentToEdit}
                toggle={closeDocumentModal}
                onComplete={documentsQuery.refetch}
                documentToEdit={documentToEdit}
                supplierId={supplierId}
                productId={productId}
                askForProduct={thirdPartyMode || linkedToProduct}
                showLegalEntities={!thirdPartyMode}
                requireProduct={linkedToProduct}
                useOrganizationProducts={linkedToProduct}
                confidentialityLevels={confidentialityLevels}
                onActionDenied={(document) => {
                    if (document?.id) {
                        hideDeniedDocument(document);
                        return;
                    }

                    setCreateDenied(true);
                }}
            />

            <DeleteDocumentModal
                isOpen={!!documentToDelete}
                toggle={() => setDocumentToDelete(null)}
                onComplete={documentsQuery.refetch}
                onDenied={hideDeniedDocument}
                document={documentToDelete}
            />

            <PaginationProvider
                pagination={paginationFactory({
                    custom: true,
                    page: filters.pageIndex,
                    sizePerPage: filters.pageSize,
                    totalSize: itemsCount,
                    withFirstAndLast: false,
                    alwaysShowAllBtns: true,
                    prePageText: (
                        <span>
                            <i className="ri-arrow-left-s-line" /> {t('Back')}
                        </span>
                    ),
                    nextPageText: (
                        <span>
                            {t('Next')} <i className="ri-arrow-right-s-line" />
                        </span>
                    ),
                    sizePerPageList: [
                        {
                            text: '25',
                            value: 25,
                        },
                        {
                            text: '50',
                            value: 50,
                        },
                    ],
                })}
            >
                {({ paginationProps, paginationTableProps }) => (
                    <>
                        <Row className="mb-4">
                            <Col sm="12">
                                <div className="d-flex justify-content-end">
                                    {showFilters && (
                                        <DocumentFilters
                                            onChange={handleFilterChange}
                                            confidentialityLevels={confidentialityLevels}
                                        />
                                    )}
                                    {showCreateButton && !createDenied && (thirdPartyMode || canWriteRows) && (
                                        <Button
                                            color="primary"
                                            outline
                                            disabled={documentsQuery.isFetching}
                                            onClick={() => {
                                                setShowDocumentModal(true);
                                            }}
                                        >
                                            {t('Add Document')}
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm="12">
                                <BootstrapTable
                                    remote={{
                                        pagination: true,
                                        filter: false,
                                        sort: true,
                                        cellEdit: false,
                                        search: false,
                                    }}
                                    loading={documentsQuery.isFetching && documentsQuery.isFetched}
                                    overlay={overlayFactory({
                                        spinner: (
                                            <Spinner
                                                animation="border"
                                                variant="primary"
                                                size="md"
                                            />
                                        ),
                                        text: `${t('Loading')}...`,
                                    })}
                                    onTableChange={handleTableChange}
                                    keyField="id"
                                    responsive
                                    bordered={false}
                                    data={documents}
                                    striped={false}
                                    columns={columns}
                                    wrapperClasses="table-responsive"
                                    classes="table tpdd-table"
                                    headerWrapperClasses="thead-light"
                                    noDataIndication={noDataIndication}
                                    {...paginationTableProps}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col sm="12">
                                <div className="tpdd-pagination-style-1">
                                    <PaginationTotalStandalone
                                        {...paginationProps}
                                    />
                                    <div>
                                        <PaginationListStandalone
                                            {...paginationProps}
                                        />
                                        <SizePerPageDropdownStandalone
                                            {...paginationProps}
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
            </PaginationProvider>
        </>
    );
};

export default withNamespaces()(connect((state) => ({
    user: state.Login.user,
}))(DocumentManager));
